import React, { useState, useEffect, useRef } from 'react';
import './Hero.css';
import TiltedCard from './TiltedCard';
import logo from '../assets/logo+.png';
import adamoImage from '../assets/Adamo.jpg';
import adamoMobileImage from '../assets/Adamo_mobile.jpg';

const Hero = () => {
  const heroRef = useRef(null);
  const [heroOpacity, setHeroOpacity] = useState(1);
  const [bubbleOpacity, setBubbleOpacity] = useState(1);
  const bubbleTimeoutRef = useRef(null);
  const isActiveRef = useRef(true);

  // Create realistic beer bubble bursts
  useEffect(() => {
    const bubblesContainer = document.getElementById('hero-bubbles-container');
    if (!bubblesContainer) return;

    isActiveRef.current = true;

    const createBubbleBurst = () => {
      if (!isActiveRef.current) return;
      
      // Random number of bubbles in this burst (3-8 bubbles)
      const bubbleCount = Math.floor(Math.random() * 6) + 3;
      // Random horizontal position for the burst
      const burstX = Math.random() * 100;
      
      for (let i = 0; i < bubbleCount; i++) {
        const bubble = document.createElement('div');
        bubble.className = 'hero-bubble';
        
        // Random size: very small bubbles (2-6px)
        const size = Math.random() * 4 + 2;
        bubble.style.width = `${size}px`;
        bubble.style.height = `${size}px`;
        
        // Random position within burst area (±2% from burst center - more compact)
        const offsetX = (Math.random() - 0.5) * 4;
        bubble.style.left = `${burstX + offsetX}%`;
        bubble.style.bottom = `${Math.random() * 10}%`; // Start from bottom 0-10% (more compact)
        
        // Random animation duration (0.8-2s for fast bubbles)
        const duration = Math.random() * 1.2 + 0.8;
        bubble.style.animationDuration = `${duration}s`;
        
        // Random delay within burst (0-0.2s - tighter burst)
        bubble.style.animationDelay = `${Math.random() * 0.2}s`;
        
        // Random horizontal drift with more variation for bubbly movement
        const drift = (Math.random() - 0.5) * 30;
        const wiggle1 = (Math.random() - 0.5) * 15;
        const wiggle2 = (Math.random() - 0.5) * 20;
        bubble.style.setProperty('--drift', `${drift}px`);
        bubble.style.setProperty('--wiggle1', `${wiggle1}px`);
        bubble.style.setProperty('--wiggle2', `${wiggle2}px`);
        
        bubblesContainer.appendChild(bubble);
        
        // Remove bubble after animation completes
        setTimeout(() => {
          if (bubble.parentNode) {
            bubble.parentNode.removeChild(bubble);
          }
        }, duration * 1000 + 300);
      }
    };

    // Create bursts at random intervals (1.5-4 seconds)
    const scheduleNextBurst = () => {
      if (!isActiveRef.current) return;
      
      createBubbleBurst();
      
      const delay = Math.random() * 4000 + 3000; // Reduced frequency: 3-7 seconds
      bubbleTimeoutRef.current = setTimeout(() => {
        scheduleNextBurst();
      }, delay);
    };

    // Create initial burst and start scheduling
    createBubbleBurst();
    scheduleNextBurst();

    return () => {
      // Cleanup: stop scheduling and remove all bubbles
      isActiveRef.current = false;
      if (bubbleTimeoutRef.current) {
        clearTimeout(bubbleTimeoutRef.current);
      }
      const container = document.getElementById('hero-bubbles-container');
      if (container) {
        container.innerHTML = '';
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const footer = document.querySelector('.footer');
      if (!footer || !heroRef.current) return;

      const footerRect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Calculate how much of the viewport the footer is covering
      const footerTop = footerRect.top;
      
      let heroOpacity = 1;
      let bubbleOpacity = 1;
      
      // When footer starts entering viewport (footer top is at or below viewport bottom)
      if (footerTop <= windowHeight) {
        // Calculate coverage: how much of the viewport is covered by footer
        // When footerTop = windowHeight: footer just entered, covering 0% -> hero 100% visible
        // When footerTop = 0: footer fully covers viewport, covering 100% -> hero 0% visible
        const viewportCoverage = (windowHeight - footerTop) / windowHeight;
        const coverage = Math.max(0, Math.min(1, viewportCoverage));
        
        // Hero and bubbles fade out as footer covers more of the viewport
        // When footer covers 0% -> opacity 1 (fully visible)
        // When footer covers 100% -> opacity 0 (fully hidden)
        heroOpacity = Math.max(0, 1 - coverage);
        bubbleOpacity = Math.max(0, 1 - coverage);
      }
      
      setHeroOpacity(heroOpacity);
      setBubbleOpacity(bubbleOpacity);
    };

    let ticking = false;
    const optimizedScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', optimizedScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', optimizedScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  return (
    <section 
      ref={heroRef}
      className="hero" 
      id="hero"
      style={{ 
        '--hero-opacity': heroOpacity,
        '--bubble-opacity': bubbleOpacity
      }}
    >
      <div className="hero-background">
        <div className="hero-adamo-image">
          <img src={adamoImage} alt="Adamo Marinacci" />
        </div>
        <div className="hero-pat-sav-image">
          <img src={adamoMobileImage} alt="Adamo Marinacci" />
        </div>
      </div>
      <div className="hero-bubbles" id="hero-bubbles-container"></div>
      <div className="hero-content container">
        <div className="hero-grid">
          <div className="hero-left">
            <h1 className="hero-title-split">
              <span className="hero-title-line">LE TEMPS</span>
              <span className="hero-title-line">D'UNE MOUSSE</span>
            </h1>
            <div className="hero-description">
              <p className="hero-description-text">
                Découvrez des conversations authentiques avec des personnalités fascinantes. 
                Chaque dimanche, <strong>Adamo Marinacci</strong> vous invite à prendre <strong>Le Temps d'une Mousse </strong> 
                pour explorer des histoires captivantes, des débats passionnants et des moments inoubliables.               
              </p>
            </div>

          </div>
          
          <div className="hero-right">
            <TiltedCard className="hero-logo-container" intensity={10}>
              <img 
                src={logo} 
                alt="Le Temps d'une Mousse" 
                className="hero-logo"
              />
            </TiltedCard>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;

